import { Bell, LogOut, Menu } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../lib/api";
import SmartSearch from "./SmartSearch";

export default function Topbar({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  // Fetch unread notification count for the badge.
  // Polls every 30s and refetches when the bell is clicked / page changes.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setUnread(list.filter((n) => !n.read).length);
      } catch {
        /* silent */
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/root/admin/login");
  };

  const goNotifications = () => {
    navigate("/root/admin/notifications");
  };

  return (
    <header
      className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-3 sm:px-5 lg:px-8 sticky top-0 z-30"
      data-testid="topbar"
    >
      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
        <button
          onClick={onToggleSidebar}
          className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded-sm hover:bg-gray-50 active:bg-gray-100 shrink-0"
          data-testid="sidebar-toggle"
          aria-label="Toggle sidebar"
        >
          <Menu size={18} />
        </button>
        {/* Search: hidden on phones, visible from md up */}
        <SmartSearch />
        {/* Mobile-only page-title hint */}
        <div className="md:hidden text-sm font-display font-bold text-gray-900 truncate">
          Maxlive88 Admin
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <button
          onClick={goNotifications}
          className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded-sm relative hover:bg-gray-50 active:bg-gray-100"
          data-testid="notifications-btn"
          aria-label="Notifications"
          title={unread > 0 ? `${unread} unread notification${unread > 1 ? "s" : ""}` : "Notifications"}
        >
          <Bell size={16} />
          {unread > 0 ? (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="notif-unread-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          ) : (
            <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 bg-emerald-500 rounded-full" />
          )}
        </button>

        <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-gray-200">
          <div className="h-8 w-8 rounded-sm bg-gray-900 flex items-center justify-center text-white font-display text-sm font-bold">
            {(user?.name || user?.email || "A").charAt(0).toUpperCase()}
          </div>
          <div className="leading-tight">
            <div className="text-xs font-semibold text-gray-900">{user?.name || "Admin"}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{user?.role || "admin"}</div>
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="h-9 flex items-center gap-1.5 px-2.5 sm:px-3 border border-gray-200 rounded-sm text-sm hover:bg-gray-50"
          data-testid="logout-btn"
          aria-label="Logout"
        >
          <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
}
